// ── DuplicateCheck — the clearance lookup ────────────────────────────────────
// A restricted BDO can only see their own accounts, which creates an obvious
// problem: they can't tell whether a company is already being worked by someone
// else. This closes that gap without opening the book.
//
// It reports existence and owner only — no link, no amounts, no contact
// details. The right next step after a hit is a conversation with the owner,
// not a look at their deal, so there is deliberately nothing to click.
//
// Drop it under the company/email fields on any intake form:
//   <DuplicateCheck company={f.companyname} email={f.email} />
import { useState, useEffect, useRef } from "react";
import { B } from "../../../shared/theme";
import { Btn } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

const STAGE_TONE = (stage) => {
  const s = String(stage || "").toLowerCase();
  if (s.includes("lost") || s.includes("declined")) return { fg: "#888780", bg: "#F1EFE8" };
  if (s.includes("client") || s.includes("funded")) return { fg: "#0F6E56", bg: "#E1F5EE" };
  if (s.startsWith("lead")) return { fg: "#185FA5", bg: "#E6F1FB" };
  return { fg: "#BA7517", bg: "#FAEEDA" };
};

const since = (d) => d
  ? new Date(d).toLocaleDateString("en-US", { month: "short", year: "numeric" })
  : null;

export const DuplicateCheck = ({ company, email, auto = true }) => {
  const { api } = useApp();
  const [state, setState] = useState({ status: "idle" });
  const timer = useRef(null);
  const seq = useRef(0);

  const co = (company || "").trim();
  const em = (email || "").trim();
  // Enough to be a real lookup rather than an enumeration sweep — the server
  // enforces the same floor.
  const enough = em.includes("@") || co.length >= 3;

  const run = () => {
    if (!enough) { setState({ status: "idle" }); return; }
    const mine = ++seq.current;
    setState({ status: "checking" });
    const params = {};
    if (em.includes("@")) params.email = em;
    if (co.length >= 3) params.company = co;
    api.get("checkDuplicate", params)
      .then(r => { if (seq.current === mine) setState({ status: "done", ...r }); })
      .catch(e => { if (seq.current === mine) setState({ status: "error", message: e.message }); });
  };

  useEffect(() => {
    if (!auto) return;
    if (timer.current) clearTimeout(timer.current);
    if (!enough) { setState({ status: "idle" }); return; }
    timer.current = setTimeout(run, 600);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [co, em, auto]);

  const wrap = (bg, border, children) => (
    <div style={{ marginBottom: 14, padding: "8px 12px", borderRadius: 8, background: bg,
                  border: "0.5px solid " + border, fontSize: 12 }}>
      {children}
    </div>
  );

  if (state.status === "idle") {
    return (
      <div style={{ marginBottom: 14, fontSize: 12, color: "var(--color-text-secondary)",
                    display: "flex", alignItems: "center", gap: 8 }}>
        <span>Enter a company name or email to check whether it's already in the system.</span>
        <Btn onClick={run} disabled={!enough}>Check</Btn>
      </div>
    );
  }

  if (state.status === "checking") {
    return wrap("var(--color-background-secondary)", "var(--color-border-tertiary)",
      <span style={{ color: "var(--color-text-secondary)" }}>Checking…</span>);
  }

  if (state.status === "error") {
    return wrap("#FAECE7", "#E8B4A8",
      <span style={{ color: "#993C1D" }}>Couldn't run the check: {state.message}</span>);
  }

  if (!state.found) {
    return wrap("#E1F5EE", "#B9E3D4",
      <span style={{ color: "#0F6E56", fontWeight: 600 }}>✓ Not in the system — clear to proceed.</span>);
  }

  return wrap("#FAEEDA", "#EBD3A0", <>
    <div style={{ fontWeight: 700, color: "#7a5200", marginBottom: 6 }}>
      Already in the system — {state.hits.length} match{state.hits.length === 1 ? "" : "es"}
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {state.hits.map((h, i) => {
        const tone = STAGE_TONE(h.stage);
        return (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)",
                           textTransform: "uppercase", minWidth: 54 }}>{h.matchtype}</span>
            <span style={{ fontWeight: 600 }}>{h.companyname}</span>
            <span style={{ color: tone.fg, background: tone.bg, padding: "1px 8px",
                           borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{h.stage}</span>
            {/* Referral sources carry a rating. "Jane has it and it's an A" is
                what decides whether asking for an introduction is worth it. */}
            {h.rating && (
              <span title="Rating" style={{ color: "#7a5200", background: "#F6E3B8", padding: "1px 7px",
                                            borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                {h.rating}
              </span>
            )}
            <span style={{ color: "var(--color-text-secondary)" }}>
              owner: <strong style={{ color: B.cyanDark }}>{h.owner}</strong>
              {since(h.since) ? " · since " + since(h.since) : ""}
            </span>
          </div>
        );
      })}
    </div>
    {state.restricted && (
      <div style={{ marginTop: 8, color: "#7a5200" }}>
        You don't have access to these records. Talk to the owner before working this one — an
        administrator can reassign it if it should be yours.
      </div>
    )}
  </>);
};
